import { z } from 'zod';
import { moneySchema } from '../money';

/** Zahlung auf eine Rechnung (Debitor) oder Eingangsrechnung (Kreditor). */
export const createPaymentSchema = z.object({
  /** Tatsächlicher Zahlungseingang. */
  amount: moneySchema,
  paidAt: z.string().datetime().optional(),
  note: z.string().trim().max(500).optional(),
  /**
   * Skonto, das mit dieser Zahlung ausgebucht wird (ADR 0108) — nur auf
   * Kundenrechnungen mit Skonto-Kondition, Zahldatum innerhalb der Frist,
   * einmal je Rechnung, höchstens der Anspruch auf den offenen Betrag
   * (`cashDiscountOffer`/`cashDiscountForPayment`). Weglassen oder 0 = kein Skonto.
   */
  cashDiscount: moneySchema.optional(),
});
export type CreatePaymentInput = z.infer<typeof createPaymentSchema>;

export const paymentResponseSchema = z.object({
  id: z.string().uuid(),
  /** Beleg-ID (Debitor: SalesDocument, Kreditor: PurchaseOrder). */
  invoiceId: z.string().uuid(),
  invoiceNumber: z.string(),
  /** Tatsächlicher Zahlungseingang. */
  amount: moneySchema,
  /** Mit dieser Zahlung ausgebuchtes Skonto (0 = keins). Beglichen = amount + cashDiscount. */
  cashDiscount: moneySchema,
  paidAt: z.string().datetime(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type PaymentResponse = z.infer<typeof paymentResponseSchema>;

/** Debitor (Forderung, Verkaufsrechnung) vs. Kreditor (Verbindlichkeit, Eingangsrechnung). */
export const openItemDirectionSchema = z.enum(['RECEIVABLE', 'PAYABLE']);
export type OpenItemDirection = z.infer<typeof openItemDirectionSchema>;

/** Offener Posten (OP-Liste): Beleg (Debitor/Kreditor) mit Zahlstand. */
export const openItemResponseSchema = z.object({
  /** Beleg-ID (Debitor: SalesDocument, Kreditor: PurchaseOrder EINGANGSRECHNUNG). */
  invoiceId: z.string().uuid(),
  direction: openItemDirectionSchema,
  /** Belegart als String (Verkauf: INVOICE/…, Einkauf: EINGANGSRECHNUNG). */
  kind: z.string(),
  number: z.string(),
  /** Kunde (Debitor) bzw. Lieferant (Kreditor). */
  partyName: z.string(),
  documentDate: z.string().datetime(),
  dueDate: z.string().datetime().nullable(),
  status: z.string(),
  gross: moneySchema,
  /** Summe der Zahlungseingänge (ohne Skonto). */
  paid: moneySchema,
  /** Ausgebuchtes Skonto (ADR 0108); offen = brutto − bezahlt − Skonto. */
  cashDiscount: moneySchema,
  open: moneySchema,
  isOverdue: z.boolean(),
  /** Skonto-Kondition des Belegs (nur Debitoren), null = kein Skonto. */
  cashDiscountPercent: z.number().nullable(),
  /** Letzter Skonto-Tag `YYYY-MM-DD` (Europe/Berlin, `cashDiscountDeadline`), null = kein Skonto. */
  cashDiscountDeadline: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
});
export type OpenItemResponse = z.infer<typeof openItemResponseSchema>;
